import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Lightning, LinkSimple, ChartBar, ShieldCheck, ShieldStar, Envelope, Clock, Plugs, IdentificationCard } from "@phosphor-icons/react";
import WelcomeDialog from "./WelcomeDialog";
import TourOverlay, { TourLauncher } from "./TourOverlay";
import { BudgetBanner } from "./BudgetCard";
import AuthBlock from "./AuthBlock";

const nav = [
  { to: "/", label: "Generator", icon: Lightning, testid: "nav-generator" },
  { to: "/links", label: "My Links", icon: LinkSimple, testid: "nav-links" },
  { to: "/connections", label: "Connections", icon: Plugs, testid: "nav-connections" },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance" },
  { to: "/surveys", label: "Surveys", icon: Envelope, testid: "nav-surveys" },
  { to: "/schedules", label: "Schedules", icon: Clock, testid: "nav-schedules" },
  { to: "/leads", label: "Lead Slips", icon: IdentificationCard, testid: "nav-leads" },
  { to: "/dmarc", label: "DMARC", icon: ShieldStar, testid: "nav-dmarc" },
  { to: "/analytics", label: "Analytics", icon: ChartBar, testid: "nav-analytics" },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const [tourOpen, setTourOpen] = useState(false);

  const handleDemoSeeded = () => {
    // Nudge them to /links so they see the seeded affiliate link + can copy the tracker
    navigate("/links");
  };

  return (
    <div className="app-shell">
      <BudgetBanner />
      <WelcomeDialog
        onDemoSeeded={handleDemoSeeded}
        onStartTour={() => setTourOpen(true)}
      />
      <TourOverlay open={tourOpen} onClose={() => setTourOpen(false)} />
      <TourLauncher onLaunch={() => setTourOpen(true)} />
      {/* Sidebar */}
      <aside
        className="w-64 flex-shrink-0 border-r border-white/10 bg-[#050505] hidden md:flex flex-col z-20"
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-white/10 flex items-center gap-3">
          <div className="w-8 h-8 bg-[#ccff00] flex items-center justify-center text-black">
            <Lightning size={20} weight="fill" />
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight">
              ADFORGE
            </div>
            <div className="label-mono text-[10px] text-zinc-500">
              affiliate ad engine
            </div>
          </div>
        </div>

        <nav className="flex-1 py-6 flex flex-col gap-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end
              data-testid={n.testid}
              className={({ isActive }) =>
                `mx-3 px-4 py-3 flex items-center gap-3 border transition-colors ${
                  isActive
                    ? "bg-[#ccff00]/10 border-[#ccff00]/30 text-[#ccff00]"
                    : "bg-transparent border-transparent text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              <span className="font-medium tracking-wide">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-6 border-t border-white/10">
          <div className="label-mono">powered by</div>
          <div className="font-mono text-xs mt-1 text-zinc-300">
            claude-sonnet · nano-banana
          </div>
        </div>
        <AuthBlock />
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        <header className="sticky top-0 z-10 backdrop-blur-2xl bg-[#050505]/70 border-b border-white/10 px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="md:hidden flex items-center gap-2">
            <div className="w-7 h-7 bg-[#ccff00] flex items-center justify-center text-black">
              <Lightning size={16} weight="fill" />
            </div>
            <span className="font-display font-bold">ADFORGE</span>
          </div>
          <div className="label-mono hidden md:block">
            drop a url · generate high-ctr ads · track clicks
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 font-mono text-xs text-zinc-500">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              live
            </div>
          </div>
        </header>
        <div className="px-6 md:px-10 py-8 md:py-12">{children}</div>
      </main>
    </div>
  );
}
